import type { OnErrorCallback } from "@bugsnag/js"

/**
 * Discard errors originating from Google Translate proxied pages.
 * Google Translate serves our site under *.translate.goog domains. Bugsnag's
 * Allowed Domains config only checks the reporting domain, not the page URL,
 * so translate.goog events bypass that filter and create significant noise.
 *
 * Also discard chunk load failures (Didomi's CDN chunks, our own dynamic imports). They come from
 * client-side network failures like slow connections or ad blockers, which aren't actionable.
 *
 * Also discard a stack overflow in the browser when none of its frames come from our bundles. It
 * seems a script that some iOS browsers inject into the page recurses until the stack runs out,
 * and WebKit reports the page URL as that script's file. We can't fix a script we don't serve.
 * This checks the message as well, so a third-party script's RangeError about our input still
 * reaches us. Next serves our bundles under `/_next/static/` whatever the asset prefix is, so the
 * path finds our frames on any host. Server frames are never under that path, so the server
 * skips this.
 *
 * Returning false tells Bugsnag to drop the event entirely.
 *
 * See https://linear.app/theconversation/issue/TC-3138
 */
export const onError: OnErrorCallback = (event) => {
  const url = event.request?.url ?? ""
  if (url.includes(".translate.goog")) return false

  const error = event.errors[0]
  if (error?.errorClass === "ChunkLoadError") return false

  const stackOverflow =
    error?.errorClass === "RangeError" &&
    error.errorMessage.includes("Maximum call stack size exceeded")
  if (
    event.app.type === "browser" &&
    stackOverflow &&
    !error.stacktrace.some((frame) => frame.file?.includes("/_next/static/"))
  ) {
    return false
  }
}
